/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect, useRef } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Component, ComponentType } from '../../types';
import PdfSearchModal from './PdfSearchModal';
import BidBodyDetailsModal, { BidBodyDetails, CollectionItemDetails } from './BidBodyDetailsModal';
import './CreateBid.css';

// Import TinyMCE
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/skins/ui/oxide/skin.css';

// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';

const CreateBid: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const editorRef = useRef<any>(null);

  // Check if we're in edit mode
  const locationState = location.state as { bidId?: number; isEditMode?: boolean } | null;
  const editingBidId = locationState?.bidId;
  const isEditMode = locationState?.isEditMode && editingBidId;
  const [formData, setFormData] = useState({
    name: '',
    internalNotes: '',
    filePath: '',
    dueDate: '',
    coverSheet: '',
    archiveUntilDate: '',
    margin: '',
    discountPercentage: '',
    discountFlat: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedComponents, setSelectedComponents] = useState<Component[]>([]);
  const [bidBodies, setBidBodies] = useState<BidBodyDetails[]>([]);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [selectedComponentForDetails, setSelectedComponentForDetails] = useState<Component | null>(null);
  const [editingComponentIndex, setEditingComponentIndex] = useState<number | null>(null);
  const [expandedSummary, setExpandedSummary] = useState<{ component: Component; totalCount: number; contributions: { name: string; count: number }[] }[]>([]);
  const [hoveredSummaryIndex, setHoveredSummaryIndex] = useState<number | null>(null);

  // Effect to calculate expanded component summary (expands collections to individual items)
  // Uses actual_count from BidBodyCollectionItems when available, otherwise falls back to suggestedCount
  useEffect(() => {
    const calculateExpandedSummary = async () => {
      const aggregated = new Map<number, { component: Component; totalCount: number; contributions: { name: string; count: number }[] }>();

      for (const bidBody of bidBodies) {
        const component = selectedComponents.find(c => c.id === bidBody.componentId);
        if (!component) continue;

        const bidBodyCount = bidBody.count || 1;

        if (component.type === ComponentType.COLLECTION) {
          // Check if we have collectionItems with actualCount (from edit mode or newly added)
          if (bidBody.collectionItems && bidBody.collectionItems.length > 0) {
            // Use actual counts from collectionItems
            for (const collItem of bidBody.collectionItems) {
              const itemCount = (collItem.actualCount || 1) * bidBodyCount;

              // We need to fetch the component details if not already known
              if (aggregated.has(collItem.componentId)) {
                const entry = aggregated.get(collItem.componentId)!;
                entry.totalCount += itemCount;
                entry.contributions.push({ name: component.name, count: itemCount });
              } else {
                // Fetch component details for display
                try {
                  const componentResponse = await apiClient.getComponentById(collItem.componentId);
                  if (componentResponse.success && componentResponse.component) {
                    aggregated.set(collItem.componentId, {
                      component: componentResponse.component,
                      totalCount: itemCount,
                      contributions: [{ name: component.name, count: itemCount }]
                    });
                  }
                } catch (err) {
                  console.error('Error fetching component for collection item:', err);
                }
              }
            }
          } else {
            // Fallback to fetching collection items with suggested counts
            try {
              const itemsResponse = await apiClient.getCollectionItems(component.id);
              if (itemsResponse.success && itemsResponse.items) {
                for (const item of itemsResponse.items) {
                  const itemComponent = item.component;
                  const itemCount = (item.suggestedCount || 1) * bidBodyCount;

                  if (aggregated.has(itemComponent.id)) {
                    const entry = aggregated.get(itemComponent.id)!;
                    entry.totalCount += itemCount;
                    entry.contributions.push({ name: component.name, count: itemCount });
                  } else {
                    aggregated.set(itemComponent.id, {
                      component: itemComponent,
                      totalCount: itemCount,
                      contributions: [{ name: component.name, count: itemCount }]
                    });
                  }
                }
              }
            } catch (err) {
              console.error('Error fetching collection items:', err);
            }
          }
        } else {
          // Regular ITEM - add directly
          if (aggregated.has(component.id)) {
            const entry = aggregated.get(component.id)!;
            entry.totalCount += bidBodyCount;
            entry.contributions.push({ name: component.name, count: bidBodyCount });
          } else {
            aggregated.set(component.id, {
              component,
              totalCount: bidBodyCount,
              contributions: [{ name: component.name, count: bidBodyCount }]
            });
          }
        }
      }

      setExpandedSummary(Array.from(aggregated.values()));
    };

    if (bidBodies.length > 0 && selectedComponents.length > 0) {
      calculateExpandedSummary();
    } else {
      setExpandedSummary([]);
    }
  }, [bidBodies, selectedComponents]);

  // State for calculated total price (updated asynchronously for collections)
  const [totalPrice, setTotalPrice] = useState(0);

  // Calculate total price from selected components with discounts and counts
  // For collections, uses actual_count from BidBodyCollectionItems
  useEffect(() => {
    const calculateTotalPrice = async () => {
      let total = 0;

      for (const bidBody of bidBodies) {
        const component = selectedComponents.find(p => p.id === bidBody.componentId);
        if (!component) continue;

        let itemPrice = 0;
        const bidBodyCount = bidBody.count || 1;

        // Check if this is a collection with actual counts
        if (component.type === ComponentType.COLLECTION && bidBody.collectionItems && bidBody.collectionItems.length > 0) {
          // Calculate price based on individual component prices * actual counts
          for (const collItem of bidBody.collectionItems) {
            try {
              const componentResponse = await apiClient.getComponentById(collItem.componentId);
              if (componentResponse.success && componentResponse.component && componentResponse.component.price) {
                itemPrice += componentResponse.component.price * (collItem.actualCount || 1);
              }
            } catch (err) {
              console.error('Error fetching component price:', err);
            }
          }
          // Multiply by bidBody count for multiple copies of the collection
          itemPrice = itemPrice * bidBodyCount;
        } else if (component.price) {
          // Regular ITEM or collection without collectionItems - use component's price
          itemPrice = component.price * bidBodyCount;
        }

        // Apply percentage discount first
        if (bidBody.discountPercentage) {
          itemPrice = itemPrice * (1 - bidBody.discountPercentage / 100);
        }

        // Apply flat discount
        if (bidBody.discountFlat) {
          itemPrice = itemPrice - bidBody.discountFlat;
        }

        total += Math.max(0, itemPrice);
      }

      setTotalPrice(total);
    };

    calculateTotalPrice();
  }, [bidBodies, selectedComponents]);

  // Auto-generate file path and default archive date when component mounts
  useEffect(() => {
    // Calculate default archive date (5 years from now)
    const fiveYearsFromNow = new Date();
    fiveYearsFromNow.setFullYear(fiveYearsFromNow.getFullYear() + 5);
    const archiveDateString = fiveYearsFromNow.toISOString().slice(0, 16);

    setFormData(prev => ({
      ...prev,
      archiveUntilDate: archiveDateString
    }));

    if (currentUser && currentUser.company) {
      const companyName = currentUser.company.name.replace(/\s+/g, '_');
      const username = currentUser.username;
      const generatedPath = `${companyName}/${username}/`;
      setFormData(prev => ({
        ...prev,
        filePath: generatedPath
      }));
    }
  }, [currentUser]);

  // Load bid data when in edit mode
  useEffect(() => {
    if (isEditMode && editingBidId) {
      const loadBidData = async () => {
        try {
          setIsLoading(true);
          setError(null);

          // Fetch bid details (includes bid bodies)
          const bidResponse = await apiClient.getBidById(editingBidId);
          if (!bidResponse.success || !bidResponse.bid) {
            setError('Failed to load bid data');
            return;
          }

          const bid = bidResponse.bid;

          // Format dates for datetime-local input
          const formatDateForInput = (dateString?: string) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              return date.toISOString().slice(0, 16);
            } catch {
              return '';
            }
          };

          // Populate form data
          setFormData({
            name: bid.name || '',
            internalNotes: bid.internalNotes || '',
            filePath: bid.filePath || '',
            dueDate: formatDateForInput(bid.dueDate),
            coverSheet: bid.coverSheet || '',
            archiveUntilDate: formatDateForInput(bid.archiveUntilDate),
            margin: bid.margin?.toString() || '',
            discountPercentage: (bid as any).discountPercentage?.toString() || '',
            discountFlat: (bid as any).discountFlat?.toString() || '',
          });

          // Set TinyMCE editor content
          if (editorRef.current && bid.coverSheet) {
            editorRef.current.setContent(bid.coverSheet);
          }

          // Process bid bodies from bid response (they're already included)
          if ((bid as any).bidBodies && Array.isArray((bid as any).bidBodies)) {
            const bodies = (bid as any).bidBodies;

            // Filter out bodies with invalid componentIds (API returns pdfId for backwards compatibility)
            const validBodies = bodies.filter((body: any) => body.pdfId != null && body.pdfId !== 0);

            // Fetch component details for each valid bid body
            const componentPromises = validBodies.map((body: any) => apiClient.getComponentById(body.pdfId));
            const componentResponses = await Promise.all(componentPromises);

            const components: Component[] = [];
            const bidBodyDetails: BidBodyDetails[] = [];

            componentResponses.forEach((response, index) => {
              if (response.success && response.component) {
                components.push(response.component);
                const body = validBodies[index];

                // Map collectionItems if present
                let collectionItems: CollectionItemDetails[] | undefined;
                if (body.collectionItems && Array.isArray(body.collectionItems)) {
                  collectionItems = body.collectionItems.map((ci: any) => ({
                    componentId: ci.componentId,
                    actualCount: ci.actualCount || 1
                  }));
                }

                bidBodyDetails.push({
                  componentId: body.pdfId,
                  sequenceNumber: body.sequenceNumber,
                  count: body.count || 1,
                  identificationCode: body.identificationCode || '',
                  discountPercentage: body.discountPercentage || 0,
                  discountFlat: body.discountFlat || 0,
                  notes: body.notes || '',
                  collectionItems,
                  frontPage: body.frontPage || undefined
                });
              }
            });

            setSelectedComponents(components);
            setBidBodies(bidBodyDetails);
          }
        } catch (err: any) {
          console.error('Error loading bid data:', err);
          setError(err.message || 'Failed to load bid data');
        } finally {
          setIsLoading(false);
        }
      };

      loadBidData();
    }
  }, [isEditMode, editingBidId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Bid name is required');
      return;
    }

    setIsLoading(true);

    try {
      const request: any = {
        name: formData.name.trim(),
      };

      if (formData.internalNotes.trim()) {
        request.internalNotes = formData.internalNotes.trim();
      }

      // Set calculated price from selected PDFs
      if (totalPrice > 0) {
        request.price = totalPrice;
      }

      if (formData.filePath.trim()) {
        request.filePath = formData.filePath.trim();
      }

      if (formData.dueDate) {
        request.dueDate = new Date(formData.dueDate).toISOString();
      }

      // Get cover sheet content from TinyMCE editor
      // Wait for any pending image uploads to complete first
      if (editorRef.current) {
        console.log('[CreateBid] Waiting for pending image uploads...');

        // Try uploadImages() if available
        if (typeof editorRef.current.uploadImages === 'function') {
          await editorRef.current.uploadImages();
        }

        // Additional wait to ensure TinyMCE has updated the DOM
        await new Promise(resolve => setTimeout(resolve, 500));

        console.log('[CreateBid] All uploads complete, getting content...');
        let coverSheetContent = editorRef.current.getContent();

        // Check for blob: URLs which indicate incomplete uploads
        if (coverSheetContent.includes('blob:')) {
          console.warn('[CreateBid] Content still contains blob: URLs, waiting longer...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          coverSheetContent = editorRef.current.getContent();
        }

        console.log('[CreateBid] Cover sheet content length:', coverSheetContent.length);
        console.log('[CreateBid] Cover sheet preview:', coverSheetContent.substring(0, 500));
        if (coverSheetContent.trim()) {
          request.coverSheet = coverSheetContent;
        }
      }

      if (formData.archiveUntilDate) {
        request.archiveUntilDate = new Date(formData.archiveUntilDate).toISOString();
      }

      if (formData.margin && parseFloat(formData.margin) > 0) {
        request.margin = parseFloat(formData.margin);
      }

      if (formData.discountPercentage && parseFloat(formData.discountPercentage) > 0) {
        request.discountPercentage = parseFloat(formData.discountPercentage);
      }

      if (formData.discountFlat && parseFloat(formData.discountFlat) > 0) {
        request.discountFlat = parseFloat(formData.discountFlat);
      }

      // Add bid bodies with all details
      if (bidBodies.length > 0) {
        request.bidBodies = bidBodies;
      }

      // Log the request before sending
      console.log('[CreateBid] About to send request with coverSheet:', request.coverSheet?.substring(0, 500));
      console.log('[CreateBid] Full request object:', JSON.stringify(request).substring(0, 1000));

      // Call appropriate API based on mode
      const response = isEditMode && editingBidId
        ? await apiClient.updateBid(editingBidId, request)
        : await apiClient.createBid(request);

      if (response.success) {
        navigate('/bids');
      } else {
        setError(response.error || `Failed to ${isEditMode ? 'update' : 'create'} bid`);
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} bid:`, err);
      setError(err.response?.data?.error || err.message || `An error occurred while ${isEditMode ? 'updating' : 'creating'} the bid`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/bids');
  };

  const moveComponentUp = (index: number) => {
    if (index > 0) {
      const newComponents = [...selectedComponents];
      const newBodies = [...bidBodies];

      [newComponents[index - 1], newComponents[index]] = [newComponents[index], newComponents[index - 1]];
      [newBodies[index - 1], newBodies[index]] = [newBodies[index], newBodies[index - 1]];

      // Update sequence numbers
      newBodies[index - 1].sequenceNumber = index;
      newBodies[index].sequenceNumber = index + 2;

      setSelectedComponents(newComponents);
      setBidBodies(newBodies);
    }
  };

  const moveComponentDown = (index: number) => {
    if (index < selectedComponents.length - 1) {
      const newComponents = [...selectedComponents];
      const newBodies = [...bidBodies];

      [newComponents[index], newComponents[index + 1]] = [newComponents[index + 1], newComponents[index]];
      [newBodies[index], newBodies[index + 1]] = [newBodies[index + 1], newBodies[index]];

      // Update sequence numbers
      newBodies[index].sequenceNumber = index + 1;
      newBodies[index + 1].sequenceNumber = index + 2;

      setSelectedComponents(newComponents);
      setBidBodies(newBodies);
    }
  };

  const removeComponent = (index: number) => {
    const newComponents = selectedComponents.filter((_, i) => i !== index);
    const newBodies = bidBodies.filter((_, i) => i !== index);

    // Update sequence numbers for remaining items
    newBodies.forEach((body, i) => {
      body.sequenceNumber = i + 1;
    });

    setSelectedComponents(newComponents);
    setBidBodies(newBodies);
  };

  const handleSelectComponent = (component: Component) => {
    setSelectedComponentForDetails(component);
  };

  const handleSaveBidBodyDetails = (details: BidBodyDetails) => {
    if (selectedComponentForDetails) {
      setSelectedComponents([...selectedComponents, selectedComponentForDetails]);
      setBidBodies([...bidBodies, details]);
    }
    setSelectedComponentForDetails(null);
    setShowComponentModal(false);
  };

  const handleCancelBidBodyDetails = () => {
    setSelectedComponentForDetails(null);
  };

  const handleEditComponentDetails = (index: number) => {
    setEditingComponentIndex(index);
  };

  const handleUpdateBidBodyDetails = (details: BidBodyDetails) => {
    if (editingComponentIndex !== null) {
      const newBodies = [...bidBodies];
      newBodies[editingComponentIndex] = details;
      setBidBodies(newBodies);
      setEditingComponentIndex(null);
    }
  };

  return (
    <div className="create-bid-container">
      <BackButton to="/bids" />
      <div className="create-bid-header">
        <h2>{isEditMode ? 'Edit Bid' : 'Create New Bid'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="create-bid-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="name">
            Bid Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={isLoading}
            required
            placeholder="Enter bid name"
            autoFocus
          />
        </div>

        <div className="form-group">
          <label htmlFor="filePath">File Path</label>
          <input
            type="text"
            id="filePath"
            name="filePath"
            value={formData.filePath}
            readOnly
            disabled
            placeholder="Auto-generated from company and username"
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Filename will be automatically generated as: Bid Name + Date
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="price">Price (Calculated)</label>
          <input
            type="text"
            id="price"
            name="price"
            value={`$${totalPrice.toFixed(2)}`}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Automatically calculated from selected PDFs
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="margin">Margin (%)</label>
          <input
            type="number"
            id="margin"
            name="margin"
            value={formData.margin}
            onChange={handleChange}
            disabled={isLoading}
            placeholder="0.00"
            step="0.01"
            min="0"
            max="100"
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Percentage applied after totals and discounts (not displayed in generated PDF)
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="discountPercentage">Discount %</label>
          <input
            type="number"
            id="discountPercentage"
            name="discountPercentage"
            value={formData.discountPercentage}
            onChange={handleChange}
            disabled={isLoading}
            placeholder="0.00"
            step="0.01"
            min="0"
            max="100"
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Percentage discount applied to the total bid price
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="discountFlat">Discount Amount ($)</label>
          <input
            type="number"
            id="discountFlat"
            name="discountFlat"
            value={formData.discountFlat}
            onChange={handleChange}
            disabled={isLoading}
            placeholder="0.00"
            step="0.01"
            min="0"
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Flat dollar amount discount applied to the total bid price
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="dueDate">Due Date</label>
          <input
            type="datetime-local"
            id="dueDate"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="archiveUntilDate">Archive Until Date</label>
          <input
            type="datetime-local"
            id="archiveUntilDate"
            name="archiveUntilDate"
            value={formData.archiveUntilDate}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>

        {/* Aggregated Component Summary - Shows individual items (collections are expanded) */}
        {expandedSummary.length > 0 && (
          <div className="form-group">
            <label>Component Summary</label>
            <div style={{
              padding: '1rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              backgroundColor: '#f0f9ff',
              position: 'relative'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: '#374151' }}>Manufacturer Code</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: '#374151' }}>Component Name</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#374151' }}>Total Count</th>
                  </tr>
                </thead>
                <tbody>
                  {expandedSummary.map(({ component, totalCount, contributions }, index) => (
                    <tr
                      key={component.id}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        backgroundColor: hoveredSummaryIndex === index ? '#e0f2fe' : 'transparent',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={() => setHoveredSummaryIndex(index)}
                      onMouseLeave={() => setHoveredSummaryIndex(null)}
                    >
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace', color: '#1f2937' }}>
                        {component.manufacturerCode || 'N/A'}
                      </td>
                      <td style={{ padding: '0.5rem', color: '#4b5563', position: 'relative' }}>
                        {component.name}
                        {hoveredSummaryIndex === index && (
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            backgroundColor: '#1f2937',
                            color: '#fff',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                            zIndex: 10,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            marginBottom: '4px'
                          }}>
                            {contributions.map((c, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                                <span>{c.name}</span>
                                <span>{c.count}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: '#1f2937' }}>
                        {totalCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Included Components</label>
          <button
            type="button"
            onClick={() => setShowComponentModal(true)}
            disabled={isLoading}
            className="add-pdf-button"
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem',
              marginBottom: '1rem'
            }}
          >
            + Add Component
          </button>

          {selectedComponents.length > 0 && (
            <div className="pdf-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {selectedComponents.map((component, index) => {
                const bidBody = bidBodies[index];
                return (
                  <div
                    key={component.id}
                    style={{
                      padding: '1rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      backgroundColor: '#f9fafb'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div
                        className="pdf-item-clickable"
                        onClick={() => handleEditComponentDetails(index)}
                        title="Click to edit component details"
                      >
                        <div className="pdf-item-name">
                          <span>#{index + 1} - {component.name}</span>
                        </div>
                        <div className="pdf-item-info">
                          {component.manufacturerName || component.manufacturerCode ? (
                            <>
                              {component.manufacturerName || 'N/A'} | {component.manufacturerCode || 'N/A'}
                            </>
                          ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No manufacturer info</span>
                          )}
                          {component.price && ` | $${component.price.toFixed(2)}`}
                        </div>
                        {bidBody && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                            {bidBody.count > 1 && <div><strong>Count:</strong> {bidBody.count}</div>}
                            {bidBody.identificationCode && <div><strong>ID Code:</strong> {bidBody.identificationCode}</div>}
                            {bidBody.notes && <div><strong>Notes:</strong> {bidBody.notes}</div>}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: '1rem' }}>
                        <button
                          type="button"
                          onClick={() => moveComponentUp(index)}
                          disabled={index === 0 || isLoading}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: index === 0 ? '#e5e7eb' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '1.25rem',
                            lineHeight: 1
                          }}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveComponentDown(index)}
                          disabled={index === selectedComponents.length - 1 || isLoading}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: index === selectedComponents.length - 1 ? '#e5e7eb' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: index === selectedComponents.length - 1 ? 'not-allowed' : 'pointer',
                            fontSize: '1.25rem',
                            lineHeight: 1
                          }}
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removeComponent(index)}
                          disabled={isLoading}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '1rem'
                          }}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {selectedComponents.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontSize: '0.875rem' }}>
              No components selected. Click "Add Component" to add components to this bid.
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="coverSheet">Cover Sheet</label>
          <Editor
            onInit={(evt, editor) => editorRef.current = editor}
            licenseKey="gpl"
            initialValue=""
            init={{
              base_url: '/tinymce',
              suffix: '.min',
              height: 400,
              menubar: true,
              promotion: false,
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'help', 'wordcount'
              ],
              toolbar: 'undo redo | blocks | ' +
                'bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'image | removeformat | help',
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
              // Configure secure image upload with handler - TinyMCE 8 format
              images_upload_handler: (blobInfo, progress) => new Promise((resolve, reject) => {
                console.log('[TinyMCE] Upload handler called for:', blobInfo.filename());
                const xhr = new XMLHttpRequest();
                xhr.withCredentials = false;

                xhr.upload.onprogress = (e) => {
                  progress(e.loaded / e.total * 100);
                };

                xhr.onload = () => {
                  console.log('[TinyMCE] Upload complete, status:', xhr.status);
                  if (xhr.status === 403) {
                    reject({ message: 'HTTP Error: ' + xhr.status, remove: true });
                    return;
                  }

                  if (xhr.status < 200 || xhr.status >= 300) {
                    reject('HTTP Error: ' + xhr.status);
                    return;
                  }

                  const json = JSON.parse(xhr.responseText);
                  console.log('[TinyMCE] Response JSON:', json);

                  if (!json || typeof json.location != 'string') {
                    console.error('[TinyMCE] Invalid JSON - no location:', json);
                    reject('Invalid JSON: ' + xhr.responseText);
                    return;
                  }

                  console.log('[TinyMCE] SUCCESS - Resolving with location:', json.location);
                  resolve(json.location);
                };

                xhr.onerror = () => {
                  console.error('[TinyMCE] Upload error occurred');
                  reject('Image upload failed due to a XHR Transport error. Code: ' + xhr.status);
                };

                const formData = new FormData();
                formData.append('file', blobInfo.blob(), blobInfo.filename());

                xhr.open('POST', '/api/tinymce/upload-image');
                const token = localStorage.getItem('jwt_token');
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                console.log('[TinyMCE] Sending upload request...');
                xhr.send(formData);
              }),
              // Automatically upload all images (pasted, dropped, etc) - converts data URIs to server URLs
              automatic_uploads: true,
              // Allow pasting images from clipboard (they will be auto-uploaded)
              paste_data_images: true,
              // Don't reuse original filenames (server generates secure UUIDs)
              images_reuse_filename: false,
              // File picker for images
              file_picker_types: 'image',
              // Validate images before upload - no SVG due to XSS risks
              images_file_types: 'jpg,jpeg,png,gif,webp',
              // Convert URLs to document relative (keeps our /api/tinymce/images/ URLs intact)
              relative_urls: true,
              // Remove script host from URLs to get relative paths
              remove_script_host: true,
              // Don't convert URLs to absolute
              convert_urls: false
            }}
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="internalNotes">Internal Notes</label>
          <textarea
            id="internalNotes"
            name="internalNotes"
            value={formData.internalNotes}
            onChange={handleChange}
            disabled={isLoading}
            rows={4}
            placeholder="Enter internal notes (not visible to clients)"
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="submit-button"
          >
            {isLoading
              ? (isEditMode ? 'Updating...' : 'Creating...')
              : (isEditMode ? 'Update Bid' : 'Create Bid')
            }
          </button>
        </div>
      </form>

      {showComponentModal && (
        <PdfSearchModal
          onClose={() => setShowComponentModal(false)}
          onSelectComponent={handleSelectComponent}
          excludedComponentIds={selectedComponents.map(component => component.id)}
        />
      )}

      {selectedComponentForDetails && (
        <BidBodyDetailsModal
          component={selectedComponentForDetails}
          sequenceNumber={selectedComponents.length + 1}
          onClose={handleCancelBidBodyDetails}
          onSave={handleSaveBidBodyDetails}
        />
      )}

      {editingComponentIndex !== null && selectedComponents[editingComponentIndex] && (
        <BidBodyDetailsModal
          component={selectedComponents[editingComponentIndex]}
          sequenceNumber={editingComponentIndex + 1}
          onClose={() => setEditingComponentIndex(null)}
          onSave={handleUpdateBidBodyDetails}
          initialData={bidBodies[editingComponentIndex]}
        />
      )}
    </div>
  );
};

export default CreateBid;
